import { useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';
import { events, type SrslyAnyEventHandler } from '@sly-rentals/core';
import { audit as vaultAudit } from '@sly-rentals/vault';

import { useNetwork } from '../../lib/network-context';
import { highlightedJson } from '../../lib/json-tree';

type SrslyEvent = Parameters<SrslyAnyEventHandler>[0];
type VaultEvent = Parameters<vaultAudit.SlyvaultAnyEventHandler>[0];
type AuditEvent = SrslyEvent | VaultEvent;

interface Toast {
  id: number;
  type: AuditEvent['type'];
  title: string;
  detail: string;
  signature: string;
  /** Decoded event payload — shown when the user expands "more details". */
  raw: AuditEvent;
}

// Visible at once. New events that arrive while the queue is full push the
// oldest toast off the top — same behaviour as the e-commerce "X just bought
// Y" toasts the design borrows from.
const MAX_TOASTS = 4;
const DISMISS_MS = 18000;

function trunc(addr: string, head = 4, tail = 4): string {
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

// Stardust → ATLAS (1 ATLAS = 100M Stardust). Format with up to 4 decimals,
// trailing zeros trimmed, so dust amounts still read sensibly.
function atlas(stardust: bigint): string {
  const whole = stardust / 100_000_000n;
  const frac = stardust % 100_000_000n;
  if (frac === 0n) return `${whole} ATLAS`;
  const fracStr = (Number(frac) / 100_000_000).toFixed(4).slice(2).replace(/0+$/, '');
  return `${whole}.${fracStr} ATLAS`;
}

// Capture points share ATLAS's 8-decimal dust precision.
function points(dust: bigint): string {
  const abs = dust < 0n ? -dust : dust;
  return (Number(abs) / 100_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Insert a space between camelCase boundaries and uppercase the whole thing
// — `RentalAccepted` → `RENTAL ACCEPTED`.
function formatTitle(type: string): string {
  return type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
}

function describe(evt: AuditEvent): { title: string; detail: string } {
  const title = formatTitle(evt.type);
  switch (evt.type) {
    case 'RentalAccepted':
      return { title, detail: `${trunc(evt.borrower)} · ${atlas(evt.escrow)} escrow` };
    case 'RentalSettled':
      return {
        title,
        detail: `${trunc(evt.rentalState)} · owner +${atlas(evt.ownerEarned)}${evt.isFinal ? ' · final' : ''}`,
      };
    case 'RentalCancelled':
      return {
        title,
        detail: `${trunc(evt.borrower)} · refund ${atlas(evt.refund)}`,
      };
    case 'RentalClosed':
      return {
        title,
        detail: `${trunc(evt.borrower)} · ${evt.pointsAwarded} pts`,
      };
    case 'OwnerPaid':
      return { title, detail: `${trunc(evt.owner)} · ${atlas(evt.amount)}` };
    case 'FeesPaid':
      return { title, detail: `${trunc(evt.contract)} · ${atlas(evt.feeCollected)}` };

    // Contract lifecycle
    case 'ContractCreated':
      return { title, detail: `${trunc(evt.owner)} · fleet ${trunc(evt.fleet)}` };
    case 'ContractUpdated':
      return { title, detail: `${trunc(evt.contract)} · rate ${atlas(evt.rate)}` };
    case 'StaleDecayApplied':
      return {
        title,
        detail: `${trunc(evt.contract)} · ${atlas(evt.oldRate)} → ${atlas(evt.newRate)}`,
      };
    case 'ContractFiberDoctored':
      return { title, detail: `${trunc(evt.thread)} · fiber ${evt.fiberIndex}` };
    case 'ReservationForceClosed':
      return {
        title,
        detail: `${trunc(evt.borrower)} · refund ${atlas(evt.refund)} (${atlas(evt.fromOwner)} from owner)`,
      };
    case 'ContractCloseQueued':
      return { title, detail: `${trunc(evt.contract)} · closes after active rental` };
    case 'ContractClosed':
      return { title, detail: `${trunc(evt.contract)} · ${trunc(evt.owner)}` };
    case 'DelegateSet':
      return {
        title,
        detail: evt.delegate
          ? `${trunc(evt.contract)} → ${trunc(evt.delegate)}`
          : `${trunc(evt.contract)} · cleared`,
      };

    // Reservations
    case 'ReservationPlaced': {
      const bid = evt.bidAtlas > 0n ? `${atlas(evt.bidAtlas)} bid` : `${evt.bidPoints} pts`;
      return {
        title,
        detail: `${trunc(evt.borrower)} · ${bid} · ${atlas(evt.escrow)} escrow`,
      };
    }
    case 'ReservationKnockedOff':
      return {
        title,
        detail: `${trunc(evt.displacedBorrower)} → ${trunc(evt.newBorrower)} · +${atlas(evt.bonus)} bonus`,
      };

    // Thread / automation
    case 'ThreadCreated':
      return { title, detail: `${trunc(evt.contract)} · ${evt.paymentsFreq}` };
    case 'ThreadFunded':
      return {
        title,
        detail: `${trunc(evt.contract)} · +${(Number(evt.added) / 1e9).toFixed(4)} SOL${evt.newPaymentsFreq ? ` · ${evt.newPaymentsFreq}` : ''}`,
      };
    case 'ThreadClosed':
      return { title, detail: `${trunc(evt.contract)} · automation off` };

    // Borrower
    case 'BorrowerCreated':
      return { title, detail: `${trunc(evt.borrower)}` };
    case 'BorrowerClaimed':
      return { title, detail: `${trunc(evt.borrower)} · ${atlas(evt.amount)}` };
    case 'BorrowerClosed':
      return { title, detail: `${trunc(evt.borrower)} · profile closed` };
    case 'CapturePointsChanged': {
      const sign = evt.delta < 0n ? '' : '+';
      return {
        title,
        detail: `${trunc(evt.borrower)} · ${sign}${points(evt.delta)} pts → ${points(evt.balance)} · ${evt.reason}`,
      };
    }

    // Slyvault
    case 'VaultCreatedEvent':
      return { title, detail: `${trunc(evt.creator)} · ${evt.memberCount} members` };
    case 'VaultClosedEvent':
      return { title, detail: `${trunc(evt.vault)} · ${trunc(evt.creator)}` };
    case 'VaultUpdatedEvent':
      return { title, detail: `${trunc(evt.vault)} · ${evt.updatedCount} rebalanced` };
    case 'DepositEvent':
      return {
        title,
        detail: `${trunc(evt.depositor)} · ${atlas(evt.total)}${evt.affiliateAmount > 0n ? ` · ${atlas(evt.affiliateAmount)} aff` : ''}${evt.discount > 0n ? ` · −${atlas(evt.discount)} disc` : ''}`,
      };
    case 'ClaimEvent':
      return {
        title,
        detail: `${trunc(evt.authority)} · ${atlas(evt.amount)} · ${evt.memberType}`,
      };
    case 'MemberInitializedEvent':
      return { title, detail: `nonce ${evt.nonce} · ${trunc(evt.authority)}` };
    case 'MemberApprovedEvent':
      return {
        title,
        detail: `nonce ${evt.nonce} · ${(evt.percentageBps / 100).toFixed(2)}%`,
      };
    case 'MemberRemovedEvent':
      return {
        title,
        detail: `nonce ${evt.nonce} · ${trunc(evt.authority)}${evt.claimed > 0n ? ` · claimed ${atlas(evt.claimed)}` : ''}`,
      };
    case 'MemberUpdatedEvent':
      return { title, detail: `nonce ${evt.nonce}` };
    case 'AffiliateApprovedEvent':
      return {
        title,
        detail: `parent ${evt.parentNonce} → aff ${evt.affiliateNonce} · ${(evt.percentageBps / 100).toFixed(2)}%`,
      };
    case 'AffiliateRemovedEvent':
      return {
        title,
        detail: `aff ${evt.affiliateNonce}${evt.claimed > 0n ? ` · claimed ${atlas(evt.claimed)}` : ''}`,
      };
    case 'AffiliateUpdatedEvent':
      return { title, detail: `aff ${evt.nonce}` };
  }
}

function explorerHref(signature: string, rpcUrl: string, network: string | undefined): string {
  if (network === 'mainnet-beta' || network === undefined) {
    return `https://solscan.io/tx/${signature}`;
  }
  if (network === 'devnet') {
    return `https://solscan.io/tx/${signature}?cluster=devnet`;
  }
  return `https://solscan.io/tx/${signature}?cluster=custom&customUrl=${encodeURIComponent(rpcUrl)}`;
}

export function EventToasts() {
  const net = useNetwork();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Track per-toast dismiss timers so hover can pause them and onMouseLeave
  // can resume. Using a ref keeps the timers stable across renders.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const scheduleDismiss = (id: number, ms: number = DISMISS_MS) => {
    clearTimeout(timers.current.get(id));
    const handle = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      timers.current.delete(id);
    }, ms);
    timers.current.set(id, handle);
  };

  const pauseDismiss = (id: number) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  };

  const dismiss = (id: number) => {
    pauseDismiss(id);
    setToasts(prev => prev.filter(t => t.id !== id));
    setExpanded(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleExpanded = (id: number) => {
    // Pause auto-dismiss while details are open — the user is reading the
    // payload and shouldn't have it disappear under them.
    pauseDismiss(id);
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        scheduleDismiss(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Re-register whenever the toggle flips. When `eventsEnabled` is false we
  // skip the subscription entirely; the network-context effect calls
  // `audit.closeSrslyEventListener()` to tear down the underlying WS too.
  useEffect(() => {
    if (!net.eventsEnabled) {
      timers.current.forEach(clearTimeout);
      timers.current.clear();
      setToasts([]);
      return;
    }
    let nextId = 1;
    const enqueue = (cast: AuditEvent) => {
      const { title, detail } = describe(cast);
      const id = nextId++;
      const toast: Toast = {
        id,
        type: cast.type,
        title,
        detail,
        signature: cast.signature,
        raw: cast,
      };
      setToasts(prev => [...prev.slice(-(MAX_TOASTS - 1)), toast]);
      scheduleDismiss(id);
    };
    const offSrsly = events.all.on(evt => enqueue(evt as AuditEvent));
    const offVault = vaultAudit.events.all.on(evt => enqueue(evt as AuditEvent));
    return () => {
      offSrsly();
      offVault();
      timers.current.forEach(clearTimeout);
      timers.current.clear();
    };
  }, [net.eventsEnabled]);

  if (toasts.length === 0) return null;

  return (
    <div className="event-toasts" aria-live="polite">
      {toasts.map(t => {
        const isOpen = expanded.has(t.id);
        return (
          <div
            key={t.id}
            className={`event-toast event-toast-${t.type}${isOpen ? ' is-expanded' : ''}`}
            // Pause auto-dismiss while the user is reading; resume on leave —
            // unless details are open, in which case the toggle owns scheduling.
            onMouseEnter={() => pauseDismiss(t.id)}
            onMouseLeave={() => {
              if (!isOpen) scheduleDismiss(t.id);
            }}
          >
            <div className="event-toast-row">
              <a
                className="event-toast-link"
                href={explorerHref(t.signature, net.rpcUrl, net.network)}
                target="_blank"
                rel="noopener noreferrer"
                title="Open transaction on Solscan"
              >
                <span className="event-toast-dot" aria-hidden="true" />
                <div className="event-toast-body">
                  <div className="event-toast-headline">{t.title}</div>
                  <div className="event-toast-detail">{t.detail}</div>
                </div>
              </a>
              <button
                type="button"
                className={`event-toast-more${isOpen ? ' is-open' : ''}`}
                aria-expanded={isOpen}
                aria-controls={`event-toast-raw-${t.id}`}
                title={isOpen ? 'Hide details' : 'More details'}
                aria-label={isOpen ? 'Hide details' : 'More details'}
                onClick={() => toggleExpanded(t.id)}
              >
                <Info size={14} strokeWidth={1.7} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="event-toast-close"
                aria-label="Dismiss"
                title="Dismiss"
                onClick={() => dismiss(t.id)}
              >
                <X size={14} strokeWidth={1.7} aria-hidden="true" />
              </button>
            </div>
            {isOpen && (
              <pre
                id={`event-toast-raw-${t.id}`}
                className="event-toast-raw"
                aria-label={`${t.type} raw payload`}
              >
                {highlightedJson(t.raw)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
